import { addDocument, getDocuments } from './firebaseService';

export interface AnalyticsEvent {
  id?: string;
  type: 'page_view' | 'form_submission' | 'blog_view' | 'blog_post_view';
  timestamp: string;
  userAgent: string;
  referrer: string;
  page: string;
  blogPostId?: string;
  blogPostSlug?: string;
  sessionId: string;
  visitorId: string; // Unique visitor ID for cross-session tracking
  ipHash?: string;
}

export interface VisitorStats {
  totalVisitors: number;
  formSubmissions: number;
  blogViews: number;
  blogPostViews: number;
  todayVisitors: number;
  thisWeekVisitors: number;
  thisMonthVisitors: number;
}

export interface BlogPostStats {
  id: string;
  slug: string;
  title: string;
  views: number;
  todayViews: number;
  thisWeekViews: number;
}

// Generate unique visitor ID for tracking unique users across multiple visits
export const generateUniqueVisitorId = (): string => {
  try {
    // First check if we already have a persistent visitor ID
    const storedVisitorId = localStorage.getItem('unique_visitor_id');
    if (storedVisitorId) return storedVisitorId;
    
    // Generate browser fingerprint for more accurate unique tracking
    const fingerprint = generateBrowserFingerprint();
    
    // Create unique visitor ID combining timestamp and fingerprint
    const visitorId = `visitor_${Date.now()}_${fingerprint}`;
    
    // Store persistently in localStorage (survives browser sessions)
    localStorage.setItem('unique_visitor_id', visitorId);
    localStorage.setItem('first_visit', new Date().toISOString());
    
    return visitorId;
  } catch (error) {
    console.error('Error generating visitor ID:', error);
    return `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

// Generate session ID for current session tracking
export const generateSessionId = (): string => {
  try {
    const stored = sessionStorage.getItem('visitor_session_id');
    if (stored) return stored;
    
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
    return sessionId;
  } catch (error) {
    console.error('Error generating session ID:', error);
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

// Generate browser fingerprint for unique user identification
const generateBrowserFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      window.screen.width + 'x' + window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      canvas.toDataURL()
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36);
  } catch (error) {
    console.error('Error generating fingerprint:', error);
    return Math.random().toString(36).substr(2, 9);
  }
};

// Track analytics event
export const trackEvent = async (eventData: Omit<AnalyticsEvent, 'timestamp' | 'userAgent' | 'referrer' | 'sessionId' | 'visitorId'>): Promise<void> => {
  try {
    const event: Omit<AnalyticsEvent, 'id'> = {
      ...eventData,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      sessionId: generateSessionId(),
      visitorId: generateUniqueVisitorId()
    };

    await addDocument('analytics', event);
  } catch (error) {
    console.error('Error tracking analytics event:', error);
  }
};

// Track page view
export const trackPageView = async (page: string): Promise<void> => {
  try {
    await trackEvent({
      type: 'page_view',
      page
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track form submission
export const trackFormSubmission = async (page: string): Promise<void> => {
  try {
    await trackEvent({
      type: 'form_submission',
      page
    });
  } catch (error) {
    console.error('Error tracking form submission:', error);
  }
};

// Track blog view
export const trackBlogView = async (): Promise<void> => {
  try {
    await trackEvent({
      type: 'blog_view',
      page: '/blog'
    });
  } catch (error) {
    console.error('Error tracking blog view:', error);
  }
};

// Track individual blog post view
export const trackBlogPostView = async (blogPostId: string, blogPostSlug: string): Promise<void> => {
  try {
    await trackEvent({
      type: 'blog_post_view',
      page: `/blog/${blogPostSlug}`,
      blogPostId,
      blogPostSlug
    });
  } catch (error) {
    console.error('Error tracking blog post view:', error);
  }
};

// Get visitor statistics
export const getVisitorStats = async (): Promise<VisitorStats> => {
  try {
    const events = await getDocuments('analytics') as AnalyticsEvent[];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Get unique visitors using visitor IDs (not sessions)
    // This ensures same user visiting multiple times = 1 unique visitor
    const uniqueVisitors = new Set(events.map(event => event.visitorId || event.sessionId));
    
    const todayEvents = events.filter(event => new Date(event.timestamp) >= today);
    const thisWeekEvents = events.filter(event => new Date(event.timestamp) >= thisWeek);
    const thisMonthEvents = events.filter(event => new Date(event.timestamp) >= thisMonth);

    // Count unique visitors for time periods
    const todayUniqueVisitors = new Set(todayEvents.map(event => event.visitorId || event.sessionId));
    const thisWeekUniqueVisitors = new Set(thisWeekEvents.map(event => event.visitorId || event.sessionId));
    const thisMonthUniqueVisitors = new Set(thisMonthEvents.map(event => event.visitorId || event.sessionId));

    return {
      totalVisitors: uniqueVisitors.size,
      formSubmissions: events.filter(event => event.type === 'form_submission').length,
      blogViews: events.filter(event => event.type === 'blog_view').length,
      blogPostViews: events.filter(event => event.type === 'blog_post_view').length,
      todayVisitors: todayUniqueVisitors.size,
      thisWeekVisitors: thisWeekUniqueVisitors.size,
      thisMonthVisitors: thisMonthUniqueVisitors.size
    };
  } catch (error) {
    console.error('Error getting visitor stats:', error);
    return {
      totalVisitors: 0,
      formSubmissions: 0,
      blogViews: 0,
      blogPostViews: 0,
      todayVisitors: 0,
      thisWeekVisitors: 0,
      thisMonthVisitors: 0
    };
  }
};

// Get blog post statistics
export const getBlogPostStats = async (): Promise<BlogPostStats[]> => {
  try {
    const [events, blogPosts] = await Promise.all([
      getDocuments('analytics'),
      getDocuments('blogPosts')
    ]);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const blogPostViews = events.filter(event => event.type === 'blog_post_view');
    
    return blogPosts.map(post => {
      const postViews = blogPostViews.filter(event => event.blogPostId === post.id);
      const todayViews = postViews.filter(event => new Date(event.timestamp) >= today);
      const thisWeekViews = postViews.filter(event => new Date(event.timestamp) >= thisWeek);

      return {
        id: post.id,
        slug: post.slug,
        title: post.title,
        views: postViews.length,
        todayViews: todayViews.length,
        thisWeekViews: thisWeekViews.length
      };
    }).sort((a, b) => b.views - a.views);
  } catch (error) {
    console.error('Error getting blog post stats:', error);
    return [];
  }
};

// Get recent activity (last 50 events)
export const getRecentActivity = async (): Promise<AnalyticsEvent[]> => {
  try {
    const events = await getDocuments('analytics');
    return (events as AnalyticsEvent[])
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 50);
  } catch (error) {
    console.error('Error getting recent activity:', error);
    return [];
  }
};

// Get detailed analytics with unique visitor tracking
export const getDetailedAnalytics = async (): Promise<{
  recentActivity: Array<{
    type: string;
    page: string;
    timestamp: string;
    blogPostSlug?: string;
  }>;
  topPosts: Array<{ slug: string; views: number }>;
  conversionRate: number;
  todayStats: {
    visitors: number;
    formSubmissions: number;
    conversionRate: number;
  };
}> => {
  try {
    const events = await getDocuments('analytics') as AnalyticsEvent[];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Recent activity (last 50 events)
    const recentActivity = events
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 50)
      .map(event => ({
        type: event.type,
        page: event.page,
        timestamp: event.timestamp,
        blogPostSlug: event.blogPostSlug
      }));

    // Top performing blog posts
    const blogPostViews = events.filter(event => event.type === 'blog_post_view');
    const blogPostStats = blogPostViews.reduce((acc, event) => {
      const slug = event.blogPostSlug || 'unknown';
      acc[slug] = (acc[slug] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topPosts = Object.entries(blogPostStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([slug, views]) => ({ slug, views }));

    // Conversion rate using unique visitors (not sessions)
    // This ensures same user visiting 5-10 times = 1 unique visitor
    const totalUniqueVisitors = new Set(events.map(event => event.visitorId || event.sessionId)).size;
    const totalFormSubmissions = events.filter(event => event.type === 'form_submission').length;
    const conversionRate = totalUniqueVisitors > 0 ? (totalFormSubmissions / totalUniqueVisitors) * 100 : 0;

    // Today's stats using unique visitors
    const todayEvents = events.filter(event => new Date(event.timestamp) >= today);
    const todayUniqueVisitors = new Set(todayEvents.map(event => event.visitorId || event.sessionId)).size;
    const todayFormSubmissions = todayEvents.filter(event => event.type === 'form_submission').length;
    const todayConversionRate = todayUniqueVisitors > 0 ? (todayFormSubmissions / todayUniqueVisitors) * 100 : 0;

    return {
      recentActivity,
      topPosts,
      conversionRate: Math.round(conversionRate * 100) / 100,
      todayStats: {
        visitors: todayUniqueVisitors,
        formSubmissions: todayFormSubmissions,
        conversionRate: Math.round(todayConversionRate * 100) / 100
      }
    };
  } catch (error) {
    console.error('Error getting detailed analytics:', error);
    return {
      recentActivity: [],
      topPosts: [],
      conversionRate: 0,
      todayStats: {
        visitors: 0,
        formSubmissions: 0,
        conversionRate: 0
      }
    };
  }
};

// Check if current user is a returning visitor
export const isReturningVisitor = (): boolean => {
  try {
    const firstVisit = localStorage.getItem('first_visit');
    if (!firstVisit) return false;
    
    const firstVisitTime = new Date(firstVisit);
    const now = new Date();
    
    // If first visit was more than 1 hour ago, consider returning visitor
    return (now.getTime() - firstVisitTime.getTime()) > (60 * 60 * 1000);
  } catch (error) {
    console.error('Error checking returning visitor:', error);
    return false;
  }
};

// Get current user's visit count (for testing purposes)
export const getCurrentVisitorId = (): string => {
  try {
    return generateUniqueVisitorId();
  } catch (error) {
    console.error('Error getting current visitor ID:', error);
    return 'unknown';
  }
}; 